import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import api, { formatApiError, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Upload, Sparkles, CalendarClock, Crown } from "lucide-react";

const PLAN_NAMES = {
  free: { label: "Free", color: "bg-slate-100 text-slate-700" },
  visitor_79: { label: "India Resident (£79)", color: "bg-blue-100 text-blue-800" },
  basic_199: { label: "Connect (£199)", color: "bg-amber-100 text-amber-900" },
  premium_399: { label: "Verified (£399)", color: "bg-emerald-100 text-emerald-900" },
  elite_699: { label: "Elite Match-Maker (£699)", color: "bg-[#6B1D2F] text-white" },
};

export default function Dashboard() {
  const { user, refresh } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [mm, setMm] = useState(null);
  const [mmLoading, setMmLoading] = useState(false);
  const photoRef = useRef(null);
  const galleryRef = useRef(null);

  useEffect(() => {
    if (user?.plan === "elite_699") loadMatchmaker();
    // eslint-disable-next-line
  }, [user?.plan]);

  const loadMatchmaker = async () => {
    setMmLoading(true);
    try {
      const r = await api.get("/matchmaker/suggestions");
      setMm(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setMmLoading(false); }
  };

  const uploadPhoto = async (file, kind) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/upload/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const path = r.data.path;
      if (kind === "profile") {
        await api.post("/profiles/search", {}); // no-op to keep pattern; we update via a dedicated endpoint below
      }
      // Update profile fields via direct Mongo endpoint — simpler: call a small update
      const update = kind === "profile" ? { profile_photo_path: path } : { gallery_paths: [...(user.gallery_paths || []), path] };
      await api.post("/profiles/update-photos", update);
      toast.success("Photo uploaded");
      await refresh();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setUploading(false); }
  };

  if (!user) return null;

  const planInfo = PLAN_NAMES[user.plan] || PLAN_NAMES.free;
  const expiry = user.plan_expires_at ? new Date(user.plan_expires_at).toLocaleDateString() : null;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl sm:text-4xl font-semibold text-slate-900">Welcome, {user.full_name?.split(" ")[0]}</h1>
          <p className="text-slate-600 mt-1">Manage your profile, photos, and plan.</p>
        </div>
        <Badge className={`${planInfo.color} text-sm px-4 py-2 rounded-full`} data-testid="dashboard-plan-badge">
          <Crown className="w-4 h-4 mr-1" /> {planInfo.label}
          {expiry && <span className="ml-2 opacity-80">· until {expiry}</span>}
        </Badge>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-8">
        <Card className="p-6 border-[#EAE3D2]">
          <h3 className="font-serif text-xl text-slate-900 mb-3">Profile photo</h3>
          <div className="h-48 rounded-lg bg-[#F3EFE6] overflow-hidden mb-3">
            {user.profile_photo_path ? (
              <img src={fileUrl(user.profile_photo_path)} alt="me" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <span className="font-serif text-5xl text-[#6B1D2F]/50">{user.full_name?.[0]}</span>
              </div>
            )}
          </div>
          <input ref={photoRef} type="file" accept="image/*" hidden data-testid="upload-photo-input" onChange={(e) => uploadPhoto(e.target.files?.[0], "profile")} />
          <Button data-testid="upload-photo-btn" disabled={uploading} onClick={() => photoRef.current?.click()} variant="outline" className="w-full border-[#6B1D2F]/30 text-[#6B1D2F]">
            <Upload className="w-4 h-4 mr-2" /> {uploading ? "Uploading…" : "Change photo"}
          </Button>
        </Card>

        <Card className="p-6 border-[#EAE3D2]">
          <h3 className="font-serif text-xl text-slate-900 mb-3">Gallery</h3>
          <div className="grid grid-cols-3 gap-2">
            {(user.gallery_paths || []).slice(0, 6).map((p) => (
              <div key={p} className="aspect-square bg-[#F3EFE6] rounded overflow-hidden">
                <img src={fileUrl(p)} alt="g" className="w-full h-full object-cover" />
              </div>
            ))}
            {(!user.gallery_paths || user.gallery_paths.length === 0) && (
              <p className="col-span-3 text-sm text-slate-500">No gallery photos yet.</p>
            )}
          </div>
          <input ref={galleryRef} type="file" accept="image/*" hidden data-testid="upload-gallery-input" onChange={(e) => uploadPhoto(e.target.files?.[0], "gallery")} />
          <Button data-testid="upload-gallery-btn" disabled={uploading} onClick={() => galleryRef.current?.click()} variant="outline" className="w-full mt-3 border-[#6B1D2F]/30 text-[#6B1D2F]">
            <Upload className="w-4 h-4 mr-2" /> Add gallery photo
          </Button>
        </Card>

        <Card className="p-6 border-[#EAE3D2] plan-card-featured">
          <div className="flex items-center gap-2 text-[#B8860B] text-xs uppercase tracking-widest font-semibold"><CalendarClock className="w-4 h-4" /> Your plan</div>
          <h3 className="font-serif text-2xl text-slate-900 mt-2">{planInfo.label}</h3>
          {expiry ? (
            <p className="text-sm text-slate-600 mt-1">Active until <b>{expiry}</b>.</p>
          ) : (
            <p className="text-sm text-slate-600 mt-1">You're on the free plan. Upgrade to unlock contact & share codes.</p>
          )}
          <Link to="/pricing"><Button data-testid="dashboard-upgrade-btn" className="mt-4 w-full bg-[#6B1D2F] hover:bg-[#541624] text-white rounded-full">View plans</Button></Link>
          <Link to="/verification"><Button variant="outline" data-testid="dashboard-verify-btn" className="mt-2 w-full rounded-full border-[#6B1D2F]/30 text-[#6B1D2F]">Get Verified Badge</Button></Link>
          <Link to="/blocked"><Button variant="ghost" data-testid="dashboard-blocked-btn" className="mt-2 w-full rounded-full text-slate-600 hover:text-[#6B1D2F]">Blocked members</Button></Link>
        </Card>
      </div>

      {user.plan === "elite_699" && (
        <Card className="p-6 border-2 border-[#D4AF37] plan-card-featured mt-6">
          <div className="flex items-center gap-2 text-[#B8860B] text-xs uppercase tracking-widest font-semibold"><Sparkles className="w-4 h-4" /> AI Match-Maker · Claude Sonnet 5</div>
          <h3 className="font-serif text-2xl text-slate-900 mt-2">This week's picks for you</h3>
          {mmLoading && <p className="text-sm text-slate-600 mt-2">Curating personalised matches…</p>}
          {mm?.insight && (
            <p className="mt-3 text-slate-800 italic leading-relaxed border-l-2 border-[#D4AF37] pl-4" data-testid="matchmaker-insight">"{mm.insight}"</p>
          )}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-5">
            {(mm?.suggestions || []).map((s) => (
              <Link to={`/profile/${s.id}`} key={s.id} data-testid={`mm-suggestion-${s.id}`}>
                <div className="bg-white rounded-lg p-4 border border-[#EAE3D2] hover:shadow-md transition-shadow">
                  <p className="font-serif text-lg text-slate-900">{s.full_name}, {s.age}</p>
                  <p className="text-sm text-slate-600">{s.profession} · {s.current_city}</p>
                  <p className="text-xs text-[#B8860B] mt-1">{s.caste} · {s.visa_type}</p>
                </div>
              </Link>
            ))}
          </div>
        </Card>
      )}

      <Card className="p-6 border-[#EAE3D2] bg-white mt-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-serif text-xl text-slate-900">Your profile summary</h3>
          <Link to="/profile/edit"><Button variant="outline" size="sm" data-testid="dashboard-edit-profile-btn" className="rounded-full border-[#6B1D2F]/30 text-[#6B1D2F]">Edit profile</Button></Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 text-sm">
          <Row k="Email" v={user.email} />
          <Row k="Mobile" v={user.mobile} />
          <Row k="Age" v={user.age} />
          <Row k="Community" v={user.caste} />
          <Row k="Profession" v={`${user.profession} · ${user.company}`} />
          <Row k="Visa" v={`${user.visa_type} (until ${user.visa_expiry_date})`} />
          <Row k="Current city" v={user.current_city} />
          <Row k="Home country" v={`${user.home_country_city}, ${user.home_country}`} />
        </div>
      </Card>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex flex-col py-1">
      <span className="text-xs uppercase tracking-wider text-slate-500">{k}</span>
      <span className="text-slate-800 font-medium">{v || "—"}</span>
    </div>
  );
}
